import React, { useState, useEffect } from "react";
import { useFetchCenturyExpiryPointsLedgerMutation } from "../../../features/tenant/Century/centuryApi";
import { useFetchStateListV2Mutation } from "../../../features/tenant/pincode/pincodeApi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import toast from "react-hot-toast";
import { HiOutlineDownload } from "react-icons/hi";
import { States } from "../../../utils/getStates";
import dayjs from "dayjs";

const CenturyExpiryPoints = () => {
  // Selection -> Point Type: My Point or Arjun Points
  const pointTypeOptions = [
    { name: "My Point", value: "mypoint" },
    { name: "Arjun Points", value: "arjunpoint" },
  ];
  const [selectedPointType, setSelectedPointType] = useState(pointTypeOptions[0]);

  // Selection -> User Type: Stockist / Sub-Stockist / Retailers / Facilitator / OEM
  const defaultUserTypes = [
    { name: "Stockist", user_type: "stockist" },
    { name: "Sub-Stockist", user_type: "substockist" },
    { name: "Retailers", user_type: "retailer" },
    { name: "Facilitator", user_type: "facilitator" },
    { name: "OEM", user_type: "oem" },
  ];
  const [userTypes, setUserTypes] = useState(defaultUserTypes);
  const [selectedUserType, setSelectedUserType] = useState(defaultUserTypes[0]);

  // Selection -> State: All States
  const [stateOptions, setStateOptions] = useState([
    { name: "All States", id: "all" },
  ]);
  const [selectedStates, setSelectedStates] = useState([stateOptions[0]]);

  // Mobile search filter
  const [mobileSearch, setMobileSearch] = useState("");

  // Date Range: From [Date] To [Date]
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, "month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));

  // Report Metadata
  const [reportGenDate, setReportGenDate] = useState(dayjs().format("DD/MM/YYYY hh:mm:ss A"));
  const [ledgerData, setLedgerData] = useState([]);

  const [fetchExpiryPointsLedger, { isLoading }] = useFetchCenturyExpiryPointsLedgerMutation();
  const [fetchStateList] = useFetchStateListV2Mutation();

  // Sync "All States" vs individual states
  useEffect(() => {
    if (selectedStates.length > 1) {
      const hasAll = selectedStates.some((st) => st.id === "all");
      const lastSelected = selectedStates[selectedStates.length - 1];

      if (lastSelected.id === "all") {
        setSelectedStates([{ name: "All States", id: "all" }]);
      } else if (hasAll) {
        setSelectedStates(selectedStates.filter((st) => st.id !== "all"));
      }
    } else if (selectedStates.length === 0) {
      setSelectedStates([{ name: "All States", id: "all" }]);
    }
  }, [selectedStates]);

  useEffect(() => {
    const getStates = async () => {
      try {
        const vendor = JSON.parse(sessionStorage.getItem("vendorData"));
        if (vendor?.tenant_id) {
          const response = await fetchStateList({ tenant_id: vendor.tenant_id }).unwrap();
          if (response.success && response.body?.states) {
            const mapped = response.body.states.map((st) => ({ name: st, id: st }));
            setStateOptions([{ name: "All States", id: "all" }, ...mapped]);
          }
        }
      } catch (error) {
        console.error("Error fetching states", error);
        const fallback = States.map((s) => ({ name: s.name, id: s.name }));
        setStateOptions([{ name: "All States", id: "all" }, ...fallback]);
      }
    };
    getStates();

    const userTypesFromSession = sessionStorage.getItem("appUserType") || sessionStorage.getItem("userTypes");
    if (userTypesFromSession) {
      try {
        const parsed = JSON.parse(userTypesFromSession);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((t) => {
            const rawType = t.user_type || t.name;
            const displayName = rawType.charAt(0).toUpperCase() + rawType.slice(1);
            return { name: displayName, user_type: rawType };
          });
          setUserTypes(mapped);
          setSelectedUserType(mapped[0]);
        }
      } catch (e) {
        console.error("Error parsing userTypes", e);
      }
    }
  }, []);

  const handleFetch = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select valid date range");
      return;
    }
    if (selectedStates.length === 0 || !selectedUserType || !selectedPointType) {
      toast.error("Please select all filters");
      return;
    }

    try {
      const statePayload = selectedStates.some((st) => st.id === "all")
        ? ["all"]
        : selectedStates.map((st) => st.id);

      const payload = {
        slug: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        token: sessionStorage.getItem("access_token"),
        body: {
          pointType: selectedPointType.value,
          userType: selectedUserType.user_type,
          state: statePayload,
          fromDate,
          toDate,
          mobile: mobileSearch ? mobileSearch.trim() : "",
        },
      };

      const response = await fetchExpiryPointsLedger(payload).unwrap();
      if (response.success && response.body) {
        setLedgerData(response.body.ledger || []);
        setReportGenDate(response.body.reportGenerationDate || dayjs().format("DD/MM/YYYY hh:mm:ss A"));
        toast.success(response.message || "Points ledger report fetched successfully");
      } else {
        setLedgerData([]);
        toast.error(response.message || "No data found");
      }
    } catch (error) {
      console.error(error);
      setLedgerData([]);
      toast.error("Failed to fetch points ledger report");
    }
  };

  const handleDownloadCsv = () => {
    if (ledgerData.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const formattedFrom = dayjs(fromDate).format("DD-MM-YYYY");
    const formattedTo = dayjs(toDate).format("DD-MM-YYYY");
    const stateNames = selectedStates.map((st) => st.name).join(" / ");

    const csvRows = [];
    csvRows.push("Points Ledger Report");
    csvRows.push(`Selection->Point Type:,${selectedPointType.name}`);
    csvRows.push(`Selection->User Type:,${selectedUserType.name}`);
    csvRows.push(`Selection-> State:,${stateNames}`);
    if (mobileSearch) {
      csvRows.push(`Selection-> Mobile No:,${mobileSearch}`);
    }
    csvRows.push(`Date Range:,From ${formattedFrom} To ${formattedTo}`);
    csvRows.push(`Report Generation Date:,${reportGenDate}`);
    csvRows.push(""); // Spacing

    const headers = [
      "User ID",
      "User Name",
      "Mobile No",
      "City",
      "State",
      "Opening Pts",
      "Earned Points",
      "Redeem Pts",
      "Expired Pts",
      "Transfer Pts",
      "Closing Balance",
    ];
    csvRows.push(headers.join(","));

    ledgerData.forEach((row) => {
      const rowData = [
        row.user_id || row.id || "N/A",
        row.name || "N/A",
        row.mobile || "N/A",
        row.city || "N/A",
        row.state || "N/A",
        Number(row.opening_pts || 0).toFixed(2),
        Number(row.earned_points || 0).toFixed(2),
        Number(row.redeem_pts || 0).toFixed(2),
        Number(row.expired_pts || 0).toFixed(2),
        Number(row.transfer_pts || 0).toFixed(2),
        Number(row.closing_balance || 0).toFixed(2),
      ];
      csvRows.push(rowData.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Points_Ledger_Report_${selectedPointType.value}_${selectedUserType.name}_${dayjs().format("YYYY-MM-DD")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      name: "User ID",
      cell: (row) => <span>{row.user_id || row.id || "N/A"}</span>,
      sortable: true,
      width: "110px",
    },
    {
      name: "User Name",
      cell: (row) => <span>{row.name || "N/A"}</span>,
      sortable: true,
    },
    {
      name: "Mobile No",
      cell: (row) => <span>{row.mobile || "N/A"}</span>,
      sortable: true,
      width: "130px",
    },
    {
      name: "City",
      cell: (row) => <span>{row.city || "N/A"}</span>,
      sortable: true,
    },
    {
      name: "State",
      cell: (row) => <span>{row.state || "N/A"}</span>,
      sortable: true,
    },
    {
      name: "Opening Pts",
      cell: (row) => <span>{Number(row.opening_pts || 0).toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Earned Points",
      cell: (row) => <span>{Number(row.earned_points || 0).toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Redeem Pts",
      cell: (row) => <span>{Number(row.redeem_pts || 0).toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Expired Pts",
      cell: (row) => <span>{Number(row.expired_pts || 0).toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Transfer Pts",
      cell: (row) => <span>{Number(row.transfer_pts || 0).toFixed(2)}</span>,
      sortable: true,
    },
    {
      name: "Closing Balance",
      cell: (row) => <span>{Number(row.closing_balance || 0).toFixed(2)}</span>,
      sortable: true,
    },
  ];

  return (
    <div className="flex w-full bg-gray-100 h-full justify-center">
      <div className="w-full">
        {/* Header Bar */}
        <div className="border-b bg-white border-gray-300 pl-6 py-3 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
          Points Ledger Report
          <LinksComponent />
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 space-y-6">
            {/* Metadata Banner */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 flex flex-wrap justify-between items-center text-sm gap-2 text-blue-900">
              <div className="space-y-1">
                <p>
                  <strong className="font-semibold">Selection-&gt;Point Type:</strong>{" "}
                  <span className="bg-blue-200 px-2 py-0.5 rounded text-blue-800 font-bold">
                    {selectedPointType.name}
                  </span>
                </p>
                <p>
                  <strong className="font-semibold">Selection-&gt;User Type:</strong>{" "}
                  <span>{selectedUserType.name}</span>
                </p>
                <p>
                  <strong className="font-semibold">Selection-&gt; State:</strong>{" "}
                  <span>{selectedStates.map((st) => st.name).join(" / ")}</span>
                </p>
              </div>
              <div className="text-right space-y-1">
                <p>
                  <strong className="font-semibold">Date Range:</strong> From{" "}
                  <span className="font-medium">{dayjs(fromDate).format("DD-MM-YYYY")}</span> To{" "}
                  <span className="font-medium">{dayjs(toDate).format("DD-MM-YYYY")}</span>
                </p>
                <p>
                  <strong className="font-semibold">Report Generation Date:</strong>{" "}
                  <span className="font-mono bg-blue-100 px-2 py-0.5 rounded text-blue-900">
                    {reportGenDate}
                  </span>
                </p>
              </div>
            </div>

            {/* Filter Form Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">Point Type</label>
                <SelectComponent
                  selected={selectedPointType}
                  setSelected={setSelectedPointType}
                  dataArr={pointTypeOptions}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">User Type</label>
                <SelectComponent
                  selected={selectedUserType}
                  setSelected={setSelectedUserType}
                  dataArr={userTypes}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">State</label>
                <MultiSelectComponent
                  required={false}
                  selected={selectedStates}
                  setSelected={setSelectedStates}
                  dataArr={stateOptions}
                  placeholder="Select State(s)..."
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">Mobile No</label>
                <input
                  type="text"
                  placeholder="Search Mobile..."
                  value={mobileSearch}
                  onChange={(e) => setMobileSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-600 uppercase">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleFetch}
                  disabled={isLoading}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isLoading ? "Loading..." : "Generate Report"}
                </button>

                {ledgerData.length > 0 && (
                  <button
                    onClick={handleDownloadCsv}
                    title="Export Report CSV"
                    className="p-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
                  >
                    <HiOutlineDownload size={20} />
                  </button>
                )}
              </div>
            </div>

            {/* Data Table */}
            <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <DataTableComponent
                columns={columns}
                data={ledgerData}
                pagination={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CenturyExpiryPoints;
